import { useEffect, useMemo, useRef, useState } from 'react';
import { AppBar, Box, Toolbar, IconButton, Menu, Avatar, Tooltip, MenuItem, ListItemIcon, Divider } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PopoverActions } from '@mui/material/Popover';

import { useTranslation } from 'react-i18next';

import { authService } from 'app/services/auth/authentication';

import { useNavigate } from 'react-router-dom';
import { ChevronRight, Logout, Palette, Refresh } from '@mui/icons-material';
import Translate from '../../../component/global/translation/translation-component';
import MenuDefinitionComponent from '../home-menu/home-menu';
import DisplayPlatformPackageComponent from '../../../component/home-page/component/app-bar/components/display-platform-package/display-platform-package-component';
import { useSession } from 'app/hooks/useSession';
import { RecordManagement } from 'app/class/record-management';
import queryKeys, { NEW_RECORD_ID } from 'shared/services/queryKeys';
import { usePlatformTheme } from 'app/providers/PlatformThemeProvider';
import { navigateFromClick } from 'shared/services/navigationClick';
import { MyWorkButton } from 'modules/my-work/components/MyWorkButton';

const AppBarComponent = () => {
    const queryClient = useQueryClient();
    const { data: userSession } = useSession();
    const platformTheme = usePlatformTheme();

    const navigate = useNavigate();
    const { t } = useTranslation();
    const menuPopoverActions = useRef<PopoverActions | null>(null);

    const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
    const [anchorElTheme, setAnchorElTheme] = useState<null | HTMLElement>(null);
    const currentPlatformPackageId = userSession?.currentPlatformPackageId;

    const { data: platformPackage, isLoading: isPlatformPackageLoading } = useQuery({
        queryKey: queryKeys.records.detail('platform_packages', currentPlatformPackageId || NEW_RECORD_ID),
        queryFn: () => new RecordManagement("platform_packages").get(currentPlatformPackageId || ""),
        retry: false,
        enabled: !!currentPlatformPackageId,
        staleTime: 1000 * 60 * 5
    });

    const platformPackageName = platformPackage?.data?.name;

    const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
        setAnchorElTheme(null);
    };

    const handleOpenThemeMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorElTheme(event.currentTarget);
    };

    const handleCloseThemeMenu = () => {
        setAnchorElTheme(null);
    };

    useEffect(() => {
        if (!anchorElUser) return;
        menuPopoverActions.current?.updatePosition();
    }, [anchorElUser, isPlatformPackageLoading, platformPackageName]);

    const userMenuSettings = useMemo(() => {
        return [
            {
                content: (
                    <>
                        <Avatar /> <Translate text="Profile" />
                    </>
                ),
                action: (event?: React.MouseEvent<HTMLElement>) => {
                    // Handle profile action
                    navigateFromClick(event, navigate, "workspace/sys_users/record/" + userSession?.id);
                }
            }
        ];
    }, [navigate, userSession]);

    const configMenuSettings = useMemo(() => {
        return [
            {
                content: (
                    <DisplayPlatformPackageComponent
                        hasPlatformPackage={!!currentPlatformPackageId}
                        isLoading={isPlatformPackageLoading}
                        packageName={platformPackageName}
                    />
                ),
                action: (event?: React.MouseEvent<HTMLElement>) => {
                    if (!currentPlatformPackageId) return;
                    navigateFromClick(event, navigate, "workspace/platform_packages/record/" + currentPlatformPackageId);
                }
            },
            {
                content: (<>
                    <ListItemIcon>
                        <Refresh fontSize="small" />
                    </ListItemIcon>
                    <Translate text="Reload data" />
                </>),
                action: () => {
                    queryClient.invalidateQueries({ refetchType: "all" });
                }
            },
            {
                content: (<>
                    <ListItemIcon>
                        <Logout fontSize="small" />
                    </ListItemIcon>
                    <Translate text="Logout" />
                </>),
                action: () => {
                    authService.logout().then(() => navigate("/login"));
                }
            }
        ];
    }, [currentPlatformPackageId, isPlatformPackageLoading, navigate, platformPackageName, platformTheme, queryClient]);

    return (
        <AppBar position="fixed" sx={{ backgroundColor: "var(--prm-appbar-bg)", color: "var(--prm-appbar-text)", borderRadius: "0px !important" }}>
            <Box sx={{ mx: { xs: 1, sm: "1%" } }}>
                <Toolbar disableGutters sx={{ minHeight: { xs: 60, sm: 68 } }}>
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Box onClick={(event) => navigateFromClick(event, navigate, "")} sx={{ display: "flex", alignItems: "center", cursor: "pointer", minWidth: 0 }}>
                            <Box
                                component="img"
                                src={platformTheme.logoMarkUrl}
                                alt="Logo"
                                sx={{ height: { xs: 38, sm: 45 }, mt: "2px" }}
                            />
                            {platformTheme.logoWordmarkUrl && (
                                <Box
                                    component="img"
                                    src={platformTheme.logoWordmarkUrl}
                                    alt="Logo"
                                    sx={{ height: 40, mt: "2px", display: { xs: "none", sm: "block" } }}
                                />
                            )}
                        </Box>

                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <MenuDefinitionComponent />
                        </Box>
                    </Box>

                    <Box sx={{ flexGrow: 0, display: "flex", alignItems: "center", gap: 1 }}>
                        <MyWorkButton />
                        <Tooltip title={t("Open settings for ") + userSession?.name}>
                            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} >
                                <Avatar alt={userSession?.name} src="#" />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            action={menuPopoverActions}
                            anchorEl={anchorElUser}
                            id="account-menu"
                            open={!!anchorElUser}
                            onClose={handleCloseUserMenu}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                    mt: 1.5,
                                    '& .MuiAvatar-root': {
                                        width: 32,
                                        height: 32,
                                        ml: -0.5,
                                        mr: 1,
                                    },
                                    '&::before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-50%) rotate(45deg)',
                                        zIndex: 0,
                                    },
                                },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            {userMenuSettings.map((menuItem, index) => (
                                <MenuItem key={"user-menu-" + index} onClick={(event) => {
                                    menuItem.action?.(event);
                                    handleCloseUserMenu();
                                }}>
                                    {menuItem.content}
                                </MenuItem>
                            ))}
                            <Divider />
                            <MenuItem onClick={handleOpenThemeMenu}>
                                <ListItemIcon>
                                    <Palette fontSize="small" />
                                </ListItemIcon>
                                <Translate text="Themes" />
                                <ChevronRight fontSize="small" sx={{ ml: "auto" }} />
                            </MenuItem>
                            <Divider />
                            {configMenuSettings.map((menuItem, index) => (
                                <MenuItem
                                    key={"config-menu-" + index}
                                    disabled={index === 0 && !currentPlatformPackageId}
                                    onClick={(event) => {
                                        menuItem.action?.(event);
                                        handleCloseUserMenu();
                                    }}
                                >
                                    {menuItem.content}
                                </MenuItem>
                            ))}
                        </Menu>
                        <Menu
                            anchorEl={anchorElTheme}
                            open={!!anchorElTheme}
                            onClose={handleCloseThemeMenu}
                            transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    mt: -0.5,
                                    minWidth: 220,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.16)",
                                },
                            }}
                        >
                            {platformTheme.themes.map((theme) => (
                                <MenuItem
                                    key={theme.id || theme.name}
                                    selected={theme.id === platformTheme.activeTheme.id}
                                    onClick={() => {
                                        platformTheme.setUserTheme(theme.id || null);
                                        handleCloseUserMenu();
                                    }}
                                >
                                    <ListItemIcon>
                                        <Palette fontSize="small" sx={{ color: theme.primary_seed_color || "var(--prm-primary)" }} />
                                    </ListItemIcon>
                                    {theme.label || theme.name}
                                </MenuItem>
                            ))}
                        </Menu>
                    </Box>
                </Toolbar>
            </Box>
        </AppBar>
    );
}
export default AppBarComponent;
